import { useLocation, useNavigate } from "@solidjs/router";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, GlobalEventData } from "@storytell/types";
import { useLayout } from "@storytell/ui/layout";
import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { DuplicateFileErrorModal } from "~/components/file-upload/DuplicateFileErrorModal";
import { FileUploadErrorModal } from "~/components/file-upload/FileUploadErrorModal";
import { UploadProgressModal } from "~/components/file-upload/UploadProgressModal";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { authGateStatus } from "~/config/authGating";
import { useI18n } from "~/i18n/context";
import { uploadFilesWithTempAssets, useFileDropHandlers, useFileUpload } from "~/lib/file-upload";
import { isUploadBatchAllReady } from "~/lib/file-upload/isUploadBatchAllReady";
import { parseFileUploadError } from "~/lib/file-upload/parseFileUploadError";
import { tryHandleScopePickerDrop } from "~/lib/scopePickerDrag";
import { createAutoLabelsForAsset } from "~/utils/assetLabels";
import { useWire } from "~/wire";
import { FullScreenDropzone } from "../prompt/internal/components/FullScreenDropzone";
import { PromptBarMode, useAppUI } from "./minimal/AppUIContext";

const OPEN_ASSETS_WITH_SELECTION_AND_LABELS = "open-assets-with-selection-and-open-labels" as const;

/** When the pointer is over the scope picker's drop zone, don't activate the full-screen overlay so the scope picker can receive the drop. */
const isOverScopePickerDropZone = (e: DragEvent) =>
  (e.target as Element)?.closest?.("[data-scope-picker-drop-zone]") != null;

export const AppLayoutDropzone = () => {
  const wire = useWire();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, actions } = useAppUI();
  const { t } = useI18n();
  const layout = useLayout();

  const [validationErrorModal, setValidationErrorModal] = createSignal({
    isOpen: false,
    title: "",
    message: "",
    filenames: [] as string[],
    // Asset IDs are only populated for the in-queue error case; validation
    // errors (file too large, unsupported type, etc.) leave this empty so the
    // modal renders Close-only.
    assetIds: [] as string[],
  });
  const [duplicateErrorModal, setDuplicateErrorModal] = createSignal({
    isOpen: false,
    filenames: [] as string[],
    assetIds: [] as string[],
  });

  const adoptCreatedAsset = async (assetId: string, asset: Asset) => {
    wire.assets.set(assetId, asset);
    const tempAsset = wire.chat.scope.uploadingAssets().find((a) => a.filename === asset.filename);
    if (tempAsset) {
      wire.chat.scope.removeUploadingAsset(tempAsset.id);
    }
    const currentAssets = wire.chat.scope.assets();
    const isAlreadyInScope = currentAssets.includes(assetId);
    if (!isAlreadyInScope) {
      wire.chat.scope.addAsset(assetId);
    }
    // Append a pill for the new asset to the draft prompt bar (no-op if no
    // prompt bar is mounted). Mirrors the @-mention picker behavior so users
    // see what they uploaded show up alongside any text they've typed.
    dispatchGlobalEvent("append-asset-pill-from-upload", { id: assetId });
    const authToken = wire.auth.token();
    if (authToken) {
      await createAutoLabelsForAsset(assetId as AssetId, authToken, wire);
    }
  };
  const fileUpload = useFileUpload({
    getAuthToken: () => wire.auth.token(),
    onAssetCreated: (assetId, asset) => {
      adoptCreatedAsset(assetId, asset).catch((error: unknown) =>
        console.error("Failed to add the uploaded asset", error)
      );
    },
    onError: (error) => {
      if (error.type === "duplicate" && error.filenames.length > 0) {
        setDuplicateErrorModal({
          isOpen: true,
          filenames: error.filenames,
          assetIds: error.assetIds ?? [],
        });
      } else {
        const parsed = parseFileUploadError(error, t);
        setValidationErrorModal({
          isOpen: true,
          title: parsed.title,
          message: parsed.message,
          filenames: parsed.filenames,
          assetIds: error.type === "in-queue" ? (error.assetIds ?? []) : [],
        });
      }
    },
  });

  const uploadingFiles = new Set<string>();

  // Upload progress modal: show when uploads active; user can minimize to pill or close (X)
  const [uploadModalDismissed, setUploadModalDismissed] = createSignal(false);
  const [uploadModalMinimized, setUploadModalMinimized] = createSignal(false);

  const isDropzoneMode = () => state.promptBar.mode === PromptBarMode.DROPZONE;

  const dropHandlers = useFileDropHandlers({
    canUploadAssets: () => wire.grants.canCreate("cp.assets"),
  });

  const handleDocumentDragEnter = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes("Files") && isOverScopePickerDropZone(e)) return;
    dropHandlers.onDragEnter(e);
  };

  const handleDocumentDragLeave = (e: DragEvent) => {
    dropHandlers.onDragLeave(e);
  };

  const handleDocumentDragOver = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes("Files") && isOverScopePickerDropZone(e)) return;
    dropHandlers.onDragOver(e);
  };

  const handleDocumentDrop = (e: DragEvent) => {
    if (isOverScopePickerDropZone(e)) {
      // Let the scope picker's onDrop handle it; don't consume the event here
      return;
    }
    if (tryHandleScopePickerDrop(e, wire)) {
      e.preventDefault();
      e.stopPropagation();
      actions.setPromptBarMode(PromptBarMode.NORMAL);
      return;
    }
    dropHandlers.onDrop(e);
  };

  // Single onMount: document drag listeners + global event subscriptions. One cleanup for all (avoids duplicate listeners on re-render).
  onMount(() => {
    document.addEventListener("dragenter", handleDocumentDragEnter);
    document.addEventListener("dragover", handleDocumentDragOver);
    document.addEventListener("dragleave", handleDocumentDragLeave);
    document.addEventListener("drop", handleDocumentDrop);

    const unsubscribeAssetDeleted = subscribeToGlobalEvent("asset-deleted", (ev) => {
      const detail = ev.detail as { assetIds?: string[]; assetId?: string } | undefined;
      const ids = detail?.assetIds ?? (detail?.assetId != null ? [detail.assetId] : []);
      ids.forEach((id) => fileUpload.removeUploadByAssetId(id));
    });

    const uploadDroppedFiles = async (ev: CustomEvent<GlobalEventData<"upload-files">>) => {
      const files = ev.detail.files;
      if (!files || files.length === 0) return;

      if (wire.auth?.isGuest?.()) {
        wire.ui.authGate.openModal({
          title: t("modal_signup_create_skill_title"),
          description: t("modal_signup_create_skill_desc"),
        });
        return;
      }

      const uploadGate = authGateStatus(wire.auth, "uploadAssets");
      if (uploadGate === "pending") return;
      if (uploadGate === "gated") {
        wire.ui.authGate.openModal({
          title: t("modal_signup_upload_title"),
          description: t("modal_signup_upload_desc"),
          pendingAction: {
            type: "upload",
          },
          feature: "uploadAssets",
        });
        return;
      }

      if (!wire.grants.canCreate("cp.assets")) return;

      // Gate before any UI side effects (temp assets, progress modal) so
      // users with 0 credits see the refill prompt instead of a phantom
      // upload that silently fails server-side.
      if (fileUpload.tryGateUploadByCredits()) return;

      const shouldOpenKnowledgePane = ev.detail?.openKnowledgePane === true;

      const fileKey = files.map((f) => `${f.name}-${f.size}-${f.lastModified}`).join("|");

      if (uploadingFiles.has(fileKey)) return;

      uploadingFiles.add(fileKey);

      // Show modal for this new batch (reset dismissed/minimized so modal appears for file-picker uploads)
      setUploadModalDismissed(false);
      setUploadModalMinimized(false);
      clearAutoDismissTimers();

      try {
        await uploadFilesWithTempAssets({
          files,
          fileUpload,
          projectId: wire.auth.realm().projectId,
          scope: wire.chat.scope,
          onUploadStart: () => {
            if (!wire.knowledgeUi.isOpen()) {
              wire.knowledgeUi.open();
            }
          },
        });
        // Open knowledge pane after upload completes (with delay to ensure scope updates have settled)
        if (shouldOpenKnowledgePane && files.length > 0) {
          setTimeout(() => {
            wire.knowledgeUi.open();
          }, 500);
        }
      } catch (error) {
        console.error("[AppLayoutDropzone] Upload failed:", error);
      } finally {
        uploadingFiles.delete(fileKey);
      }
    };
    const unsubscribeUploadFiles = subscribeToGlobalEvent("upload-files", (ev) => void uploadDroppedFiles(ev));

    onCleanup(() => {
      document.removeEventListener("dragenter", handleDocumentDragEnter);
      document.removeEventListener("dragover", handleDocumentDragOver);
      document.removeEventListener("dragleave", handleDocumentDragLeave);
      document.removeEventListener("drop", handleDocumentDrop);
      clearAutoDismissTimers();
      unsubscribeUploadFiles();
      unsubscribeAssetDeleted();
    });
  });

  // Derive effective upload status directly from wire.assets so the modal
  // always reflects the source of truth without waiting for the createEffect
  // bridge in useFileUpload to propagate the change.
  const uploads = () =>
    fileUpload.uploads().map((upload) => {
      if (upload.status !== "processing" || !upload.assetId) return upload;
      const asset = wire.assets.get(upload.assetId);
      const assetState = asset?.status?.state;
      if (assetState && lifecycleStateGrouped(assetState) === "ready") {
        return { ...upload, status: "completed" as const };
      }
      return upload;
    });

  const allReady = () => isUploadBatchAllReady(uploads());

  // Reset dismissed when uploads finish so next batch shows the modal again
  createEffect(() => {
    if (uploads().length === 0) {
      setUploadModalDismissed(false);
      setUploadModalMinimized(false);
      clearAutoDismissTimers();
    }
  });

  const AUTO_DISMISS_MINIMIZE_DELAY_MS = 1500;
  const AUTO_DISMISS_REMOVE_DELAY_MS = 3500;
  let autoDismissMinimizeTimerId: ReturnType<typeof setTimeout> | null = null;
  let autoDismissRemoveTimerId: ReturnType<typeof setTimeout> | null = null;

  const clearAutoDismissTimers = () => {
    if (autoDismissMinimizeTimerId !== null) {
      clearTimeout(autoDismissMinimizeTimerId);
      autoDismissMinimizeTimerId = null;
    }
    if (autoDismissRemoveTimerId !== null) {
      clearTimeout(autoDismissRemoveTimerId);
      autoDismissRemoveTimerId = null;
    }
  };

  // When the whole batch is ready, fade through the pill into a full dismiss.
  // Cancels itself if state regresses (new upload, error, manual interaction).
  createEffect(() => {
    if (allReady()) {
      clearAutoDismissTimers();
      autoDismissMinimizeTimerId = setTimeout(() => {
        setUploadModalMinimized(true);
        autoDismissMinimizeTimerId = null;
      }, AUTO_DISMISS_MINIMIZE_DELAY_MS);
      autoDismissRemoveTimerId = setTimeout(() => {
        setUploadModalDismissed(true);
        autoDismissRemoveTimerId = null;
      }, AUTO_DISMISS_REMOVE_DELAY_MS);
    } else {
      clearAutoDismissTimers();
    }
  });

  // Auto-minimize when user navigates into a chat thread so the modal doesn't cover the prompt bar.
  const isOnChatThread = () => /\/chat_[^/]+/.test(location.pathname);
  createEffect(() => {
    if (isOnChatThread()) {
      setUploadModalMinimized(true);
    }
  });

  const showUploadModal = () => uploads().length > 0 && !uploadModalDismissed();

  const getProjectId = () => wire.userContext.get()?.projectId ?? wire.auth.realm().projectId ?? "";

  // When the user clicks "Use in Chat" on the duplicate / in-queue warning:
  //   - If a prompt bar is already on screen (new-chat page or an open chat
  //     thread), add the assets to the active scope and pop a pill into the
  //     prompt bar — mirrors what onAssetCreated does after a real upload.
  //   - If we're on a page without a prompt bar (Library, settings, etc.),
  //     fall back to startNewChatWithAssets so the user lands somewhere they
  //     can actually see and send the file. Without this branch the click
  //     looks like a no-op because the pill event has no listener mounted.
  const handleUseInChat = (assetIds: string[]) => {
    const { primaryId } = wire.navigation.state();
    const promptBarVisible = !primaryId || primaryId === "new" || isOnChatThread();
    if (!promptBarVisible) {
      startNewChatWithAssets(assetIds);
      return;
    }
    const currentAssets = wire.chat.scope.assets();
    for (const id of assetIds) {
      if (!currentAssets.includes(id)) {
        wire.chat.scope.addAsset(id);
      }
      dispatchGlobalEvent("append-asset-pill-from-upload", { id });
    }
  };

  const startNewChatWithAssets = (assetIds: string[]) => {
    wire.chat.resetChat();
    actions.resetChat();
    wire.chat.scope.clearAll({ fromExplicitUserClear: true });
    for (const id of assetIds) {
      wire.chat.scope.addAsset(id);
    }
    // On the project home (no primary) or the new-chat page, the visible prompt bar is already a
    // chat starter — navigating would duplicate it in a side panel. Just update scope in place.
    const { primaryId } = wire.navigation.state();
    const isOnChatStarterPage = !primaryId || primaryId === "new";
    if (!isOnChatStarterPage) {
      wire.navigation.navigateToContent("new", layout.isSide());
    }
    setTimeout(() => dispatchGlobalEvent("focus-prompt-bar", undefined), 100);
  };

  return (
    <>
      <DuplicateFileErrorModal
        isOpen={duplicateErrorModal().isOpen}
        onClose={() => setDuplicateErrorModal({ isOpen: false, filenames: [], assetIds: [] })}
        filenames={duplicateErrorModal().filenames}
        assetIds={duplicateErrorModal().assetIds}
        onUseInChat={handleUseInChat}
      />
      <FileUploadErrorModal
        isOpen={validationErrorModal().isOpen}
        onClose={() => setValidationErrorModal({ isOpen: false, title: "", message: "", filenames: [], assetIds: [] })}
        title={validationErrorModal().title}
        message={validationErrorModal().message}
        filenames={validationErrorModal().filenames}
        assetIds={validationErrorModal().assetIds}
        onUseInChat={handleUseInChat}
      />
      <Show when={isDropzoneMode()}>
        <FullScreenDropzone />
      </Show>

      <Show when={showUploadModal()}>
        <UploadProgressModal
          uploads={uploads}
          minimized={uploadModalMinimized()}
          allReady={allReady()}
          onMinimize={() => {
            clearAutoDismissTimers();
            setUploadModalMinimized((m) => !m);
          }}
          onClose={() => setUploadModalDismissed(true)}
          getViewUrl={(_projectId, assetId) => {
            return wire.navigation.buildUrl({ projectId: getProjectId(), primaryId: "assets", sideId: assetId });
          }}
          onChatWithAsset={(_projectId, assetId) => startNewChatWithAssets([assetId])}
          onChatWithFiles={(_projectId, assetIds) => startNewChatWithAssets(assetIds)}
          onLabelFiles={(_projectId, assetIds) => {
            const projectId = getProjectId();
            navigate(wire.navigation.buildUrl({ projectId, primaryId: "assets" }));
            setTimeout(() => dispatchGlobalEvent(OPEN_ASSETS_WITH_SELECTION_AND_LABELS, { projectId, assetIds }), 450);
          }}
        />
      </Show>
    </>
  );
};
